// =============================================================================
// COMMISSIONS.JS — Deal commission calculation logic.
// =============================================================================
// This is the single source of truth for how commissions are split between
// Steve and the agent on each deal. Move it back out at your peril.
// =============================================================================

export const COMMISSION_THRESHOLD = 1000   // No commission on deals of $1K or under
export const DEFAULT_RATE = 0.20           // 20% on most deals
export const MALICK_RATE = 0.10            // Malick is a special 10% creator

// Per-creator commission floor, keyed by lowercase creator name.
//   min       — the dollar figure the deal is measured against
//   inclusive — true: a deal AT min qualifies. false: it must exceed min.
// Anyone not listed uses the default: strictly over $1,000.
//
// Mirelle's roster was moved to an inclusive $1,000 floor in September 2026,
// so a deal that lands exactly on $1K still pays. Evan was deliberately left
// on the default (he needs $1,001+), and Every Eats Possible sits at $2,000.
// The floor follows the CREATOR, not the agent — it reflects that creator's
// typical deal size, so it survives the account moving to someone else.
export const CREATOR_THRESHOLDS = {
  darcia:                { min: 1000, inclusive: true },
  revello:               { min: 1000, inclusive: true },
  subecca:               { min: 1000, inclusive: true },
  zhara:                 { min: 1000, inclusive: true },
  'every eats possible': { min: 2000, inclusive: false },
}

/** True when `amount` clears the commission floor for `creatorName`. */
export function meetsCommissionThreshold(amount, creatorName) {
  const rule = CREATOR_THRESHOLDS[(creatorName || '').toLowerCase()]
    || { min: COMMISSION_THRESHOLD, inclusive: false }
  return rule.inclusive ? amount >= rule.min : amount > rule.min
}

// Monthly qualified-sales threshold per agent (keyed by lowercase agent name).
// If the agent's total booked sales for a month land under this number, their
// commission for that whole month is forfeited. Agents not listed here always
// qualify. Deals of any size count toward the threshold (incl. under $1K).
export const MONTHLY_QUALIFIER = {
  lydie: 20000,
}

// Lydie moved from hourly (0%) to 5% commission starting with June 2026 deals.
// Deals dated before this keep the old all-to-Steve split even if re-edited.
export const LYDIE_COMMISSION_START = '2026-06-01'

// How each agent's commission is allocated against the total.
// keep: fraction of total commission the agent keeps.
// toSteve: fraction of total commission that goes to Steve.
// (Malick is a creator-level override applied on top of these splits.)
const SPLITS = {
  steve:   { keep: 1.00, toSteve: 0.00 }, // Steve as agent — keeps it all
  mirelle: { keep: 0.30, toSteve: 0.70 }, // Mirelle 6%, Steve 14% on a 20% deal (6/20 = 0.30)
  lydie:   { keep: 0.25, toSteve: 0.75 }, // Lydie 5%, Steve 15% on a 20% deal (since June 2026)
  katrina: { keep: 0.00, toSteve: 1.00 }, // Katrina hourly — all to Steve
  loizza:  { keep: 0.50, toSteve: 0.50 }, // Loizza 50/50
  vincent: { keep: 0.40, toSteve: 0.60 }, // Vincent 8%, Steve 12% on a 20% deal (8/20 = 0.40)
  lari:    { keep: 0.00, toSteve: 1.00 }, // Lari hourly — all to Steve (same as Katrina)
}

// Special case: Mirelle on a Malick deal keeps nothing (whole 10% goes to Steve).
// Lydie's 5% only applies from June 2026 — older deals stay all-to-Steve.
// Ray Tarca deals: Loizza keeps 80% of the commission, Steve 20% (not her usual 50/50).
function getEffectiveSplit(agentKey, creator, dealDate) {
  const creatorLower = (creator || '').toLowerCase()
  if (agentKey === 'mirelle' && creatorLower === 'malick') {
    return { keep: 0, toSteve: 1 }
  }
  if (agentKey === 'loizza' && creatorLower === 'ray tarca') {
    return { keep: 0.80, toSteve: 0.20 }
  }
  if (agentKey === 'lydie' && dealDate && String(dealDate) < LYDIE_COMMISSION_START) {
    return { keep: 0, toSteve: 1 }
  }
  return SPLITS[agentKey] || null
}

function getRate(creator) {
  const creatorLower = (creator || '').toLowerCase()
  return creatorLower === 'malick' ? MALICK_RATE : DEFAULT_RATE
}

/**
 * Compute commission split for a deal.
 * Returns an object with commission_total, commission_steve, and the per-agent
 * commission column (commission_mirelle / commission_loizza / etc.) as 0.
 *
 * @param {number} amount      Deal amount in dollars.
 * @param {string} agentName   Agent name as stored on the deal (e.g. 'Mirelle').
 * @param {string} creatorName Creator name as stored on the deal.
 * @param {string} [dealDate]  Deal date (YYYY-MM-DD) — used for effective-dated splits.
 * @returns {{commission_total:number, commission_steve:number, commission_mirelle:number, commission_lydie:number, commission_loizza:number}}
 */
export function computeCommissions(amount, agentName, creatorName, dealDate) {
  const zero = {
    commission_total: 0,
    commission_steve: 0,
    commission_mirelle: 0,
    commission_lydie: 0,
    commission_loizza: 0,
    commission_vincent: 0,
  }
  const amt = Number(amount) || 0
  // Most deals must be strictly OVER $1,000. Some creators carry their own
  // floor — see CREATOR_THRESHOLDS.
  if (!meetsCommissionThreshold(amt, creatorName)) return zero

  // Steve's own deals carry no commission — he is both creator and agency, so
  // there is nothing to split. (Double Shots referral cuts are tracked
  // separately via double_shots_pct and are unaffected.)
  if ((creatorName || '').toLowerCase() === 'steve') return zero

  const rate = getRate(creatorName)
  const total = +(amt * rate).toFixed(2)
  const out = { ...zero, commission_total: total }

  const agentKey = (agentName || '').toLowerCase()
  const split = getEffectiveSplit(agentKey, creatorName, dealDate)

  // No mapping (e.g. agent is empty or N/A) → no allocation.
  if (!split) return out

  const agentCut = +(total * split.keep).toFixed(2)
  const steveCut = +(total * split.toSteve).toFixed(2)
  out.commission_steve = steveCut

  // Place agent's keep into their own commission column when one exists.
  const field = `commission_${agentKey}`
  if (field in out) out[field] = agentCut

  // Steve-as-agent: his keep is also his cut. (His column is commission_steve.)
  if (agentKey === 'steve') out.commission_steve = agentCut

  return out
}

/**
 * Mutate `u` (a deal-in-progress object) with computed commission columns.
 * Convenience wrapper for the deal-edit form.
 */
export function recalcCommissionsOn(u, amount, agentName) {
  const comp = computeCommissions(amount, agentName, u.creator, u.date)
  Object.assign(u, comp)
}

// =============================================================================
// DERIVED LOOKUPS — so no other file has to restate the splits.
// =============================================================================
// Reports used to decide whose commission Steve gets an override cut from with
// a literal `agent === 'mirelle' || agent === 'lydie'`, which quietly omitted
// Loizza, Katrina, Vincent and Lari. Settings, the Excel importer and the
// deal-detail display each carried their own copy of the rules as well. These
// helpers read SPLITS directly, so adding an agent here updates every screen.

/** Agent keys the engine knows how to split a commission for. */
export const AGENT_KEYS = Object.keys(SPLITS)

/** The split for an agent, or null when the engine has no rule for them. */
export function splitFor(agentName) {
  return SPLITS[(agentName || '').toLowerCase()] || null
}

/** True when some part of this agent's commission is allocated to Steve. */
export function sharesWithSteve(agentName) {
  const key = (agentName || '').toLowerCase()
  if (!key || key === 'steve') return false
  const split = SPLITS[key]
  return !!split && split.toSteve > 0
}

/** True when the agent keeps a cut of their own (i.e. is not hourly). */
export function keepsOwnCut(agentName) {
  const split = splitFor(agentName)
  return !!split && split.keep > 0
}

/** The deal column holding this agent's own cut, or null for hourly agents. */
export function commissionFieldFor(agentName) {
  const key = (agentName || '').toLowerCase()
  if (!key) return null
  return keepsOwnCut(key) ? `commission_${key}` : null
}

/**
 * The commission rows that should be shown for a deal, derived from SPLITS.
 * Rows are returned even when the value is $0 so the slot that *should* exist
 * for that agent is still visible (an hourly agent, or a pre-June-2026 Lydie
 * deal, correctly reads zero rather than disappearing).
 */
export function commissionRowsFor(deal, colorFor) {
  const key = (deal?.agent || '').toLowerCase() || 'steve'
  const rows = []
  const split = SPLITS[key]

  // Steve always has a row: either as the agent, or as the override recipient.
  if (key === 'steve' || !split || split.toSteve > 0) {
    rows.push({ key: 'steve', label: "Steve's cut", value: deal?.commission_steve || 0 })
  }
  if (key !== 'steve' && split && split.keep > 0) {
    const name = key.charAt(0).toUpperCase() + key.slice(1)
    rows.push({ key, label: `${name}'s cut`, value: deal?.[`commission_${key}`] || 0 })
  }
  return rows.map(r => ({ ...r, color: colorFor ? colorFor(r.key) : undefined }))
}
